import { useEffect, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { sheetFetch } from '@/lib/sheetFetch'

// Generic in-cell <select> primitive used by InlineStatusEdit and the
// (future) InlinePriorityEdit. Keeps a controlled value, fires a PATCH
// against `endpoint` with `{ [paramKey]: nextValue }` via sheetFetch,
// and rolls back optimistically when the server returns non-2xx
// (validation failure, policy denial, etc).
//
// Read-only mode: when `disabled` is true we render a plain text label
// (the label resolved from `options`) without the trigger affordance —
// matches plan §Unit 12 "Read-only cells show neither (no icon, default
// cursor)".
interface Option<V extends string> {
  value: V
  label: string
}

interface InlineSelectProps<V extends string> {
  value: V
  options: readonly Option<V>[]
  endpoint: string
  paramKey: string
  disabled?: boolean
  ariaLabel: string
  // Called after a successful change. Lets parents (TaskDetailSheet,
  // standalone Show, index list rows) refresh their own state to
  // reflect the new value.
  onAfterChange: () => void
}

export function InlineSelect<V extends string>({
  value,
  options,
  endpoint,
  paramKey,
  disabled = false,
  ariaLabel,
  onAfterChange,
}: InlineSelectProps<V>) {
  const [current, setCurrent] = useState<V>(value)
  const [pending, setPending] = useState(false)

  // Re-sync local state when the prop changes (e.g. an Inertia partial
  // reload of the index list after a status change in the sheet
  // refreshes this row's `value` prop). useState only initializes once,
  // so without this the row would keep the stale optimistic value.
  useEffect(() => {
    setCurrent(value)
  }, [value])

  if (disabled) {
    const label = options.find((o) => o.value === current)?.label ?? current
    return <span className="text-sm">{label}</span>
  }

  async function handleChange(next: string) {
    if (next === current) return
    const previous = current
    setCurrent(next as V)
    setPending(true)

    try {
      const response = await sheetFetch(endpoint, {
        method: 'PATCH',
        body: { [paramKey]: next },
      })
      if (!response.ok) {
        setCurrent(previous)
      } else {
        onAfterChange()
      }
    } catch {
      setCurrent(previous)
    } finally {
      setPending(false)
    }
  }

  return (
    <Select value={current} onValueChange={handleChange} disabled={pending}>
      <SelectTrigger className="h-8 w-[140px]" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
